import { useRef, useState } from 'react'
import {
  CircleCheck,
  ExternalLink,
  GitFork,
  GitPullRequest,
  Maximize2,
  MessageSquare,
  Pencil,
  RotateCcw,
  ShieldCheck,
  Trash2,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { RichContent } from '@/components/common/rich-content'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { PriorityDot, StageDot, TypeGlyph, UserAvatar } from '@/components/common/badges'
import {
  ComboboxField,
  DateField,
  FieldRow,
  MultiComboboxField,
  SelectField,
} from './field-editors'
import { CommentComposer } from './comment-composer'
import { useUiState } from '@/hooks/use-ui-state'
import { useVerifyAffordances } from '@/hooks/use-verify-affordances'
import { useRestoreFocus } from '@/hooks/use-restore-focus'
import { MentionGuardContext } from '@/hooks/use-mention-guard'
import {
  fieldByName,
  useIssueDetail,
  useIssueFields,
  useIssues,
  useRepoMeta,
  type IssueDetail,
  type LinkedRef,
} from '@/lib/query'
import { githubIssueUrl, githubIssueUrlIn } from '@/lib/issue-url'
import {
  useDeleteIssue,
  useReopenIssue,
  useSetAssignees,
  useSetIssueField,
  useSetIssueType,
  useSetLabels,
  useUpdateIssueText,
  type FieldName,
} from '@/lib/mutations'
import { CrossContextFlags } from '@/components/common/cross-context-flags'
import { optionComparator } from '@/lib/field-order'
import { ISSUE_FIELDS, type WorkIssue } from '@/types/github'

export function InlineTitle({ issue }: { issue: WorkIssue }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(issue.title)
  const [lastTitle, setLastTitle] = useState(issue.title)
  const update = useUpdateIssueText()

  // Adopt upstream edits (poll refresh / other users) unless mid-edit.
  if (issue.title !== lastTitle) {
    setLastTitle(issue.title)
    if (!editing) setDraft(issue.title)
  }

  if (!editing) {
    return (
      <button
        type="button"
        className="group hover:border-border -mx-1 rounded border border-transparent px-1 text-left text-[15.5px] leading-snug font-semibold"
        title="Click to edit title"
        onClick={() => setEditing(true)}
      >
        {issue.title}
        <Pencil className="text-faint-foreground ml-1.5 inline size-3 align-baseline opacity-0 transition-opacity group-hover:opacity-100" />
      </button>
    )
  }
  const commit = () => {
    setEditing(false)
    if (draft.trim() && draft !== issue.title) {
      update.mutate({ issue, title: draft.trim() })
    } else {
      setDraft(issue.title)
    }
  }
  return (
    <Input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit()
        if (e.key === 'Escape') {
          setDraft(issue.title)
          setEditing(false)
        }
      }}
      className="text-[15.5px] font-semibold"
    />
  )
}

/**
 * Issue/comment content. Prefers GitHub's pre-rendered, pre-sanitised
 * `bodyHTML` (exact parity, incl. raw HTML in helpdesk-synced emails);
 * falls back to client-side markdown for optimistic updates.
 */
function RichBody({ html, markdown }: { html?: string | null; markdown: string }) {
  return <RichContent html={html} markdown={markdown} className="text-muted-foreground text-xs" />
}

export function BodyEditor({
  issue,
  body,
  bodyHTML,
}: {
  issue: WorkIssue
  body: string
  bodyHTML?: string | null
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(body)
  const [lastBody, setLastBody] = useState(body)
  const update = useUpdateIssueText()

  if (body !== lastBody) {
    setLastBody(body)
    if (!editing) setDraft(body)
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-2">
        <Textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={8}
          className="font-mono text-xs"
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            className="h-6 text-xs"
            onClick={() => {
              setEditing(false)
              if (draft !== body) update.mutate({ issue, body: draft })
            }}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 text-xs"
            onClick={() => {
              setDraft(body)
              setEditing(false)
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="group relative -mx-1 px-1">
      <button
        type="button"
        onClick={() => setEditing(true)}
        title="Edit description"
        aria-label="Edit description"
        className="text-faint-foreground hover:text-foreground hover:bg-hover absolute top-0.5 right-0.5 rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
      >
        <Pencil className="size-3" />
      </button>
      {body ? (
        <RichBody html={bodyHTML} markdown={body} />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-faint-foreground hover:text-foreground text-xs italic"
        >
          No description — add one
        </button>
      )}
    </div>
  )
}

export function RefList({ title, icon, refs }: { title: string; icon: React.ReactNode; refs: LinkedRef[] }) {
  if (refs.length === 0) return null
  return (
    <section>
      <h3 className="text-muted-foreground mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide uppercase">
        {icon}
        {title}
      </h3>
      <div className="flex flex-col gap-1">
        {refs.map((ref) => (
          <a
            key={ref.id}
            href={ref.url ?? githubIssueUrlIn(ref.repo, ref.number)}
            target="_blank"
            rel="noreferrer"
            className="border-border bg-card hover:bg-hover flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs"
          >
            <span className="text-faint-foreground font-mono text-[11px]">
              {ref.repo}#{ref.number}
            </span>
            <span className="flex-1 truncate">{ref.title}</span>
            <Badge variant="outline" className="px-1 py-0 text-[10px]">
              {ref.state.toLowerCase()}
            </Badge>
          </a>
        ))}
      </div>
    </section>
  )
}

export function Comments({ detail }: { detail: IssueDetail }) {
  return (
    <section>
      <h3 className="text-muted-foreground mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide uppercase">
        <MessageSquare className="size-3" />
        Comments
      </h3>
      {detail.comments.length === 0 ? (
        <p className="text-faint-foreground text-xs">No comments.</p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {detail.comments.map((c) => (
            <div key={c.id} className="text-xs">
              <div className="mb-0.5 flex items-center gap-1.5">
                {c.author && (
                  <UserAvatar
                    user={{ login: c.author.login, name: null, avatarUrl: c.author.avatarUrl }}
                    className="size-4"
                  />
                )}
                <span className="font-medium">{c.author?.login ?? 'ghost'}</span>
                <span className="text-faint-foreground">
                  {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
                </span>
              </div>
              <RichBody html={c.bodyHTML} markdown={c.body} />
            </div>
          ))}
        </div>
      )}
      <CommentComposer issue={detail.issue} />
    </section>
  )
}

export function FieldStack({ issue }: { issue: WorkIssue }) {
  const fields = useIssueFields().data
  const meta = useRepoMeta().data
  const setField = useSetIssueField()
  const setType = useSetIssueType()
  const setLabels = useSetLabels()
  const setAssignees = useSetAssignees()

  const saving = (name: FieldName) =>
    setField.isPending && setField.variables?.fieldName === name

  const opts = (name: string) => {
    const options = fieldByName(fields, name)?.options ?? []
    // Shared field-order rules so the picker matches the facet bar and sidebar
    // (Client alpha, Quarter chronological; everything else live field order).
    const cmp = optionComparator(name)
    return cmp ? [...options].sort((a, b) => cmp(a.name, b.name)) : options
  }

  const selectRow = (label: FieldName, clearable = true) => (
    <FieldRow label={label}>
      <SelectField
        value={issue[ISSUE_FIELDS[label].key] as string | null}
        options={opts(label)}
        clearable={clearable}
        saving={saving(label)}
        onChange={(value) => setField.mutate({ issue, fieldName: label, value })}
        renderOption={
          label === 'Stage'
            ? (name) => (
                <span className="flex items-center gap-1.5">
                  <StageDot stage={name} />
                  {name}
                </span>
              )
            : label === 'Priority'
              ? (name) => (
                  <span className="flex items-center gap-1.5">
                    <PriorityDot priority={name} />
                    {name}
                  </span>
                )
              : undefined
        }
      />
    </FieldRow>
  )

  const dateRow = (
    label: FieldName,
    key: 'targetDate' | 'promotedAt' | 'releasedAt' | 'releasedToQaAt' | 'releasedToProdAt',
  ) => (
    <FieldRow label={label}>
      <DateField
        value={issue[key]}
        saving={saving(label)}
        onChange={(value) => setField.mutate({ issue, fieldName: label, value })}
      />
    </FieldRow>
  )

  return (
    <div className="flex flex-col gap-2">
      <FieldRow label="Type">
        <SelectField
          value={issue.typeName}
          options={(meta?.issueTypes ?? []).map((t) => ({ id: t.id, name: t.name, color: null }))}
          clearable
          saving={setType.isPending}
          onChange={(typeName) => setType.mutate({ issue, typeName })}
          renderOption={(name) => (
            <span className="flex items-center gap-1.5">
              <TypeGlyph typeName={name} />
              {name}
            </span>
          )}
        />
      </FieldRow>
      {selectRow('Stage')}
      {selectRow('Priority')}
      <div>
        {selectRow('Sprint')}
        {issue.subIssueCount > 0 && (
          <p className="text-faint-foreground mt-0.5 ml-[104px] text-[10.5px]">
            Parent issue — set Sprint on its children instead.
          </p>
        )}
      </div>
      {selectRow('Quarter')}
      {selectRow('Effort')}
      {selectRow('Product')}
      {selectRow('Workload')}
      <FieldRow label="Client">
        <ComboboxField
          modal
          value={issue.client}
          options={opts('Client').map((o) => o.name)}
          saving={saving('Client')}
          onChange={(value) => setField.mutate({ issue, fieldName: 'Client', value })}
        />
      </FieldRow>
      {selectRow('Source')}
      {selectRow('Verifier')}
      {dateRow('Target date', 'targetDate')}
      {dateRow('Promoted at', 'promotedAt')}
      {/* Date rows track the live schema: legacy 'Released at' disappears
          from the Sheet when the org deletes the field. */}
      {fieldByName(fields, 'Released at') && dateRow('Released at', 'releasedAt')}
      {fieldByName(fields, 'Released to QA on') && dateRow('Released to QA on', 'releasedToQaAt')}
      {fieldByName(fields, 'Released to Prod on') && dateRow('Released to Prod on', 'releasedToProdAt')}
      <FieldRow label="Assignees">
        <MultiComboboxField
          modal
          values={issue.assignees.map((a) => a.login)}
          options={(meta?.assignableUsers ?? []).map((u) => u.login)}
          saving={setAssignees.isPending}
          onChange={(logins) => setAssignees.mutate({ issue, logins })}
        />
      </FieldRow>
      <FieldRow label="Labels">
        <MultiComboboxField
          modal
          values={issue.labels}
          options={(meta?.labels ?? []).map((l) => l.name)}
          saving={setLabels.isPending}
          onChange={(labels) => setLabels.mutate({ issue, labels })}
        />
      </FieldRow>
    </div>
  )
}

export function IssueSheet() {
  const { sheetIssue, setSheetIssue } = useUiState()
  const open = sheetIssue !== null
  const restoreFocus = useRestoreFocus(open)
  // The composer flips this while its @-mention popup is open; Escape then
  // dismisses the popup (composer's own handler) instead of closing the Sheet.
  const mentionOpen = useRef(false)
  return (
    <Sheet open={open} onOpenChange={(o) => !o && setSheetIssue(null)}>
      <SheetContent
        side="right"
        onCloseAutoFocus={restoreFocus}
        onEscapeKeyDown={(e) => {
          if (mentionOpen.current) e.preventDefault()
        }}
        className="gap-0 overflow-hidden p-0 data-[side=right]:w-[760px] data-[side=right]:sm:max-w-[92vw]"
      >
        <MentionGuardContext.Provider value={mentionOpen}>
          {sheetIssue !== null && <SheetBody number={sheetIssue} />}
        </MentionGuardContext.Provider>
      </SheetContent>
    </Sheet>
  )
}

/** Close/reopen + delete cluster. `onDeleted` dismisses the hosting surface
 * (Sheet vs full page) once a delete is confirmed. */
export function HeaderActions({
  issue,
  onDeleted,
}: {
  issue: WorkIssue
  onDeleted: () => void
}) {
  const reopen = useReopenIssue()
  const deleteIssue = useDeleteIssue()
  const { canVerifyQa, canVerifyProd, verifyQa, close } = useVerifyAffordances(issue)

  return (
    <>
      {canVerifyQa && (
        <Button
          size="sm"
          variant="outline"
          className="h-6 text-xs"
          // Stay disabled after a successful dispatch too: the marker only
          // lands when the workflow runs (refetched ~6s later), so without this
          // the button is re-clickable in that window and fires a duplicate run.
          disabled={verifyQa.isPending || verifyQa.isSuccess}
          onClick={() => verifyQa.mutate({ issue })}
        >
          <ShieldCheck className="size-3" />
          Verified in QA
        </Button>
      )}
      {issue.state === 'OPEN' ? (
        <Button
          size="sm"
          variant="outline"
          className="h-6 text-xs"
          disabled={close.isPending}
          onClick={() => close.mutate({ issue })}
        >
          {canVerifyProd ? <ShieldCheck className="size-3" /> : <CircleCheck className="size-3" />}
          {canVerifyProd ? 'Verified in production' : 'Close issue'}
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline"
          className="h-6 text-xs"
          disabled={reopen.isPending}
          onClick={() => reopen.mutate({ issue })}
        >
          <RotateCcw className="size-3" />
          Reopen
        </Button>
      )}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size="icon-sm"
            variant="ghost"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive size-6"
            title="Delete issue"
          >
            <Trash2 className="size-3.5" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete #{issue.number}?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently deletes “{issue.title}” from GitHub, including its comments and
              issue-field values. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                onDeleted()
                deleteIssue.mutate({ issue })
              }}
            >
              Delete issue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function SheetBody({ number }: { number: number }) {
  const { setSheetIssue } = useUiState()
  const navigate = useNavigate()
  const detailQuery = useIssueDetail(number)
  const universeIssue = useIssues().data?.find((i) => i.number === number)
  const issue = detailQuery.data?.issue ?? universeIssue

  if (!issue) {
    // Closed issues aren't in the open-only universe, so a failed detail
    // fetch (stale deep link, deleted issue) must say so, not skeleton forever.
    if (detailQuery.isError) {
      return (
        <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm">
          <p>Couldn't load issue #{number}.</p>
          <p className="text-faint-foreground text-xs">
            It may be closed or deleted.{' '}
            <a
              className="text-secondary hover:underline"
              href={githubIssueUrl(number)}
              target="_blank"
              rel="noreferrer"
            >
              Check on GitHub
            </a>
          </p>
        </div>
      )
    }
    return (
      <div className="flex flex-col gap-3 p-4">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <SheetHeader className="border-border flex-row items-center gap-2 space-y-0 border-b py-3 pr-10 pl-4">
        <span className="text-muted-foreground font-mono text-xs">#{issue.number}</span>
        <TypeGlyph typeName={issue.typeName} />
        {issue.state === 'CLOSED' && (
          <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
            Closed
          </Badge>
        )}
        <span className="flex-1" />
        <Button
          size="icon-sm"
          variant="ghost"
          className="size-6"
          title="Open full page"
          onClick={() => {
            setSheetIssue(null)
            navigate(`/issues/${issue.number}`)
          }}
        >
          <Maximize2 className="size-3.5" />
        </Button>
        <HeaderActions issue={issue} onDeleted={() => setSheetIssue(null)} />
        <a
          href={issue.url}
          target="_blank"
          rel="noreferrer"
          className="text-secondary inline-flex items-center gap-1 text-xs hover:underline"
        >
          Open in GitHub
          <ExternalLink className="size-3" />
        </a>
        <SheetTitle className="sr-only">Issue #{issue.number}</SheetTitle>
      </SheetHeader>
      <div className="scroll-y flex-1 px-4 py-3">
        <div className="flex flex-col gap-3">
          <InlineTitle issue={issue} />
          {issue.author && (
            <div className="text-faint-foreground flex items-center gap-1.5 text-xs">
              <UserAvatar
                user={{ login: issue.author, name: null, avatarUrl: issue.authorAvatarUrl ?? '' }}
                className="size-4"
              />
              <span>
                Opened by <span className="text-muted-foreground font-medium">{issue.author}</span>{' '}
                {formatDistanceToNow(new Date(issue.createdAt), { addSuffix: true })}
              </span>
            </div>
          )}
          <BodyEditor
            issue={issue}
            body={detailQuery.data?.body ?? issue.body}
            bodyHTML={detailQuery.data?.bodyHTML}
          />
          <Separator />
          <FieldStack issue={issue} />
          <CrossContextFlags issueId={issue.id} variant="full" />
          <Separator />
          {detailQuery.isError && (
            <p className="text-destructive text-[11px]">
              Couldn't load comments, sub-issues and linked PRs — fields above still work.
            </p>
          )}
          {detailQuery.data && (
            <>
              <RefList
                title="Sub-issues"
                icon={<GitFork className="size-3" />}
                refs={detailQuery.data.subIssues}
              />
              <RefList
                title="Linked PRs"
                icon={<GitPullRequest className="size-3" />}
                refs={detailQuery.data.linkedPrs}
              />
              <Comments detail={detailQuery.data} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
